'use client';

import { Info } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cssStyles from './TextPreview.module.css';

interface VirtualizedHtmlContentProps {
  htmlContent: string;
  sectionId: string | null;
  highlightClass: string;
  onScrollToSection?: (sectionId: string, found: boolean) => void;
  resetScrollTrigger?: number;
  showScrollbarOnHover?: boolean;
}

export function VirtualizedHtmlContent({
  htmlContent,
  sectionId,
  highlightClass,
  onScrollToSection,
  resetScrollTrigger = 0,
  showScrollbarOnHover = false,
}: VirtualizedHtmlContentProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [sectionStatus, setSectionStatus] = useState<
    'searching' | 'found' | 'not-found' | null
  >(null);
  const t = useTranslations('TextPreview');

  // Parse HTML once (expensive operation) - separate from highlighting
  const parsedDocument = useMemo(() => {
    if (!htmlContent) return null;

    try {
      const parser = new DOMParser();
      return parser.parseFromString(htmlContent, 'text/html');
    } catch {
      return null;
    }
  }, [htmlContent]); // ← Only re-parse when HTML changes (not on highlight changes)

  // Apply highlighting (cheap operation) - separated from parsing
  const { processedHtmlContent, sectionFound } = useMemo(() => {
    if (!parsedDocument || !htmlContent) {
      return { processedHtmlContent: htmlContent, sectionFound: null };
    }

    if (!sectionId) {
      return { processedHtmlContent: htmlContent, sectionFound: null };
    }

    try {
      // Clone the parsed document (cheap) instead of re-parsing (expensive)
      const docClone = parsedDocument.cloneNode(true) as Document;
      const el = docClone.getElementById(sectionId);

      if (!el) {
        return { processedHtmlContent: htmlContent, sectionFound: false };
      }

      // Add highlight class to cloned element (cheap CSS class manipulation)
      el.classList.add(highlightClass);
      return {
        processedHtmlContent: docClone.body.innerHTML,
        sectionFound: true,
      };
    } catch {
      return { processedHtmlContent: htmlContent, sectionFound: false };
    }
  }, [parsedDocument, htmlContent, sectionId, highlightClass]); // ← Now highlight changes are cheap (just clone + class add)

  // Update section status based on memo result (separate from render)
  useEffect(() => {
    if (sectionFound === null) {
      setSectionStatus(null);
    } else if (sectionFound) {
      setSectionStatus('found');
    } else {
      setSectionStatus('not-found');
    }
  }, [sectionFound]);

  // Helper function to scroll to section (simplified - no virtualization)
  const scrollToSection = useCallback(
    (targetSectionId: string, retriggerAnimation = false) => {
      if (!targetSectionId) {
        if (containerRef.current) {
          containerRef.current.scrollTop = 0;
        }
        return;
      }

      if (!containerRef.current) {
        onScrollToSection?.(targetSectionId, false);
        setSectionStatus('not-found');
        return;
      }

      // Find element directly in DOM
      const element = containerRef.current.querySelector(
        `#${CSS.escape(targetSectionId)}`
      ) as HTMLElement;

      if (element) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        const relativeTop = elementRect.top - containerRect.top;
        const relativeBottom = elementRect.bottom - containerRect.top;

        // Check if element is already fully visible
        if (
          retriggerAnimation &&
          relativeTop >= 0 &&
          relativeBottom <= containerRect.height
        ) {
          // Already visible - just animate
          element.classList.remove('activeHighlight-animated');
          void element.offsetHeight;
          element.classList.add('activeHighlight-animated');
          setTimeout(() => {
            element.classList.remove('activeHighlight-animated');
          }, 650);
        } else {
          // Scroll to element
          containerRef.current.scrollTop += relativeTop - 4;

          if (retriggerAnimation) {
            element.classList.remove('activeHighlight-animated');
            void element.offsetHeight;
            element.classList.add('activeHighlight-animated');
            setTimeout(() => {
              element.classList.remove('activeHighlight-animated');
            }, 650);
          }
        }

        onScrollToSection?.(targetSectionId, true);
        setSectionStatus('found');
      } else {
        // Element not found - try parent IDs
        let searchId = targetSectionId;
        let foundElement: HTMLElement | null = null;

        while (searchId && !foundElement) {
          const lastDotIndex = searchId.lastIndexOf('.');
          if (lastDotIndex === -1) break;

          searchId = searchId.substring(0, lastDotIndex);
          foundElement = containerRef.current.querySelector(
            `#${CSS.escape(searchId)}`
          ) as HTMLElement;
        }

        if (foundElement) {
          const containerRect = containerRef.current.getBoundingClientRect();
          const elementRect = foundElement.getBoundingClientRect();
          const relativeTop = elementRect.top - containerRect.top;
          containerRef.current.scrollTop += relativeTop - 4;
          onScrollToSection?.(targetSectionId, true);
          setSectionStatus('found');
        } else {
          containerRef.current.scrollTop = 0;
          onScrollToSection?.(targetSectionId, false);
          setSectionStatus('not-found');
        }
      }
    },
    [onScrollToSection]
  );

  // Scroll to highlighted section on mount or when section changes
  useEffect(() => {
    if (sectionId) {
      // Delay to ensure DOM is ready and CSS styles are fully applied after HTML parsing
      const timer = setTimeout(() => {
        scrollToSection(sectionId);
      }, 200); // Increased from 50ms to allow CSS styles to fully apply
      return () => clearTimeout(timer);
    }
    // No section specified - scroll to top
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
    return;
  }, [sectionId, scrollToSection]);

  // Handle reset scroll trigger (when user hovers over citation link again)
  useEffect(() => {
    if (resetScrollTrigger > 0 && sectionId) {
      scrollToSection(sectionId, true);
    }
  }, [resetScrollTrigger, sectionId, scrollToSection]);

  return (
    <div
      ref={containerRef}
      className={`p-3 ${cssStyles['documentContent']} overflow-y-auto h-full bg-surface-subtle ${
        showScrollbarOnHover
          ? '[scrollbar-gutter:stable] [&::-webkit-scrollbar]:bg-surface-subtle [&::-webkit-scrollbar-track]:bg-surface-subtle [&::-webkit-scrollbar-thumb]:bg-transparent group-hover:[&::-webkit-scrollbar-thumb]:bg-content-muted [&::-webkit-scrollbar-thumb]:transition-colors'
          : ''
      }`}
    >
      {/* Section status message */}
      {sectionStatus === 'not-found' && sectionId && (
        <div className='bg-status-warning/10 border border-status-warning/30 rounded px-3 py-2 mb-2 flex items-center gap-2'>
          <Info className='w-4 h-4 text-status-warning' />
          <span className='text-xs text-status-warning font-medium'>
            {t('sectionNotFoundShort')}
          </span>
        </div>
      )}

      {/* Render HTML directly without virtualization - preview is small enough */}
      <div dangerouslySetInnerHTML={{ __html: processedHtmlContent || '' }} />
    </div>
  );
}
